import { addAuditLog } from './audit.js'
import {
  fetchProdutos, fetchMovimentacoes,
  createMovimentacao, updateMovimentacao,
  updateProduto, invalidateCache,
  snakeToCamel, getEmpresaId
} from './syncManager.js'
import { supabase } from './supabase.js'

export const MOVEMENT_TYPES = {
  saida_emprestimo: 'saida-emprestimo',
  saida_entrega: 'saida-entrega',
  devolucao: 'devolucao',
  cancelar_entrega: 'cancelar-entrega',
  cancelar_emprestimo: 'cancelar-emprestimo'
}

export const DEVOLUCAO_TIPO = {
  devolvido: 'devolvido',
  perdido: 'perdido',
  manutencao: 'manutencao'
}

function getOperacaoPorProduto(produto) {
  if (produto.tipo === 'ferramenta') return 'emprestimo'
  if (produto.tipo === 'material') return 'entrega'
  if (produto.tipo === 'epi') return produto.reutilizavel ? 'emprestimo' : 'entrega'
  return 'entrega'
}

export async function createTransaction({
  usuario,
  funcionario,
  obra,
  itens,
  assinaturaBase64,
  fotoEntrega,
  observacaoGeral
}) {
  if (!funcionario?.id) throw new Error('Selecione um funcionário.')
  if (!Array.isArray(itens) || itens.length === 0) throw new Error('Carrinho vazio.')
  if (!assinaturaBase64) throw new Error('Assinatura obrigatória.')

  const now = Date.now()
  const produtos = await fetchProdutos()

  for (const it of itens) {
    const produto = produtos.find(p => p.id === it.produtoId)
    if (!produto) throw new Error('Produto não encontrado.')

    const qty = Number(it.quantidade)
    if (!Number.isFinite(qty) || qty <= 0) throw new Error('Quantidade inválida.')

    const atual = Number(produto.quantidade ?? 0)
    if (qty > atual) {
      throw new Error(`Estoque insuficiente: ${produto.nome} (disp: ${atual})`)
    }

    const pTipo = produto.tipo || produto.categoria
    const operacao = getOperacaoPorProduto({ ...produto, tipo: pTipo })
    const tipoMovimentacao = operacao === 'emprestimo' ? MOVEMENT_TYPES.saida_emprestimo : MOVEMENT_TYPES.saida_entrega

    if (pTipo === 'epi' && !produto.reutilizavel) {
      if (!it.motivoEpi) throw new Error(`Informe o motivo do EPI: ${produto.nome}`)
    }

    // Atualizar estoque no Supabase
    await updateProduto(produto.id, { quantidade: atual - qty })

    // Cálculo financeiro opcional
    const custoUnit = produto?.custoUnitario ?? produto?.dados?.custoUnitario ?? null
    const isConsumo = (operacao === 'entrega') && (pTipo === 'material' || (pTipo === 'epi' && !produto.reutilizavel))
    const classificacao = isConsumo ? 'consumo' : undefined
    const custoTotal = isConsumo && custoUnit != null ? Number(qty) * Number(custoUnit) : null

    // Criar movimentação no Supabase
    await createMovimentacao({
      id: crypto.randomUUID(),
      tipo: tipoMovimentacao,
      produtoId: produto.id,
      produtoNome: produto.nome,
      quantidade: qty,
      funcionarioId: funcionario.id,
      funcionarioNome: funcionario.nome,
      obraId: obra?.id ?? null,
      obraNome: obra?.nome ?? null,
      usuarioId: usuario?.usuarioId ?? null,
      usuarioNome: usuario?.usuarioNome ?? null,
      status: operacao === 'emprestimo' ? 'pendente-devolucao' : 'concluido',
      assinaturaFuncionario: assinaturaBase64,
      fotoEntrega: fotoEntrega ?? null,
      observacao: it.observacao ?? observacaoGeral ?? null,
      tipoProduto: pTipo,
      epiReutilizavel: pTipo === 'epi' ? Boolean(produto.reutilizavel) : null,
      motivoEpi: pTipo === 'epi' && !produto.reutilizavel ? it.motivoEpi : null,
      dataMovimentacao: now,
      // extras financeiros opcionais
      classificacao,
      custoUnitarioNoMomento: custoUnit != null ? Number(custoUnit) : null,
      custoTotal: custoTotal
    })
  }

  await addAuditLog({
    usuarioId: usuario?.usuarioId ?? null,
    usuarioNome: usuario?.usuarioNome ?? null,
    acao: 'MOVIMENTACAO_CARRINHO_FINALIZADA',
    detalhes: JSON.stringify({ funcionarioId: funcionario.id, itens: itens.map(i => ({ produtoId: i.produtoId, quantidade: i.quantidade })) }),
    tabelaAfetada: 'movimentacoes',
    registroId: null
  })

  return { ok: true }
}

export async function listPendingLoans() {
  const { data, error } = await supabase
    .from('movimentacoes')
    .select('id,tipo,produto_id,produto_nome,quantidade,funcionario_id,funcionario_nome,usuario_id,usuario_nome,observacao,status,criado_em,dados')
    .eq('empresa_id', getEmpresaId())
    .eq('status', 'pendente-devolucao')
  if (error) throw error
  return (data || []).map(snakeToCamel)
}

export async function registerLoanReturn({ usuario, movementId, observacao, quantidadeDevolvida }) {
  const now = Date.now()
  // Buscar movimentação por ID direto
  const { data: movData } = await supabase.from('movimentacoes').select('id,status,produto_id,quantidade,funcionario_id,funcionario_nome').eq('id', movementId).single()
  if (!movData) throw new Error('Movimentação não encontrada.')
  const mov = snakeToCamel(movData)
  if (mov.status !== 'pendente-devolucao') throw new Error('Este item não está pendente.')

  // Buscar produto por ID direto
  const { data: prodData } = await supabase.from('produtos').select('id,nome,quantidade').eq('id', mov.produtoId).single()
  if (!prodData) throw new Error('Produto não encontrado.')
  const produto = snakeToCamel(prodData)

  const atual = Number(produto.quantidade ?? 0)
  const qtyTotal = Number(mov.quantidade ?? 0)
  const qtdDevolvida = quantidadeDevolvida !== null ? Number(quantidadeDevolvida) : qtyTotal
  const qtdRestante = qtyTotal - qtdDevolvida
  
  // Atualizar estoque com quantidade devolvida
  await updateProduto(produto.id, { quantidade: atual + qtdDevolvida })

  // Se for devolução parcial, criar nova movimentação para o restante pendente
  if (qtdRestante > 0) {
    await createMovimentacao({
      tipo: 'saida-emprestimo',
      produtoId: mov.produtoId,
      produtoNome: produto.nome,
      quantidade: qtdRestante,
      funcionarioId: mov.funcionarioId,
      funcionarioNome: mov.funcionarioNome,
      usuarioId: usuario?.usuarioId ?? null,
      usuarioNome: usuario?.usuarioNome ?? null,
      status: 'pendente-devolucao'
    })
    
    // Atualizar a movimentação original com a quantidade devolvida
    await updateMovimentacao(mov.id, {
      quantidade: qtdDevolvida,
      status: 'concluido',
      devolucaoTipo: DEVOLUCAO_TIPO.devolvido,
      dataDevolucao: now,
      observacao: observacao ?? mov.observacao ?? null,
      usuarioId: usuario?.usuarioId ?? null,
      usuarioNome: usuario?.usuarioNome ?? null,
      quantidadeDevolvida: qtdDevolvida
    })
  } else {
    // Se for devolução total, atualiza a movimentação original
    await updateMovimentacao(mov.id, {
      status: 'concluido',
      devolucaoTipo: DEVOLUCAO_TIPO.devolvido,
      dataDevolucao: now,
      observacao: observacao ?? mov.observacao ?? null,
      usuarioId: usuario?.usuarioId ?? null,
      usuarioNome: usuario?.usuarioNome ?? null,
      quantidadeDevolvida: qtdDevolvida
    })
  }

  await addAuditLog({
    usuarioId: usuario?.usuarioId ?? null,
    usuarioNome: usuario?.usuarioNome ?? null,
    acao: 'DEVOLUCAO_REGISTRADA',
    detalhes: JSON.stringify({ movementId }),
    tabelaAfetada: 'movimentacoes',
    registroId: mov.id
  })
}

export async function markLoanAsLost({ usuario, movementId, observacao }) {
  const now = Date.now()
  // Buscar movimentação por ID direto
  const { data: movData } = await supabase.from('movimentacoes').select('id,status,produto_id,quantidade').eq('id', movementId).single()
  if (!movData) throw new Error('Movimentação não encontrada.')
  const mov = snakeToCamel(movData)
  if (mov.status !== 'pendente-devolucao') throw new Error('Este item não está pendente.')

  // Buscar produto para calcular custo de prejuízo (opcional)
  const { data: prodData } = await supabase.from('produtos').select('id,nome,quantidade,dados').eq('id', mov.produtoId).single()
  const produto = prodData ? snakeToCamel(prodData) : null
  const custoUnit = produto?.custoUnitario ?? produto?.dados?.custoUnitario ?? null
  const qty = Number(mov.quantidade ?? 0)
  const custoTotal = custoUnit != null ? qty * Number(custoUnit) : null

  await updateMovimentacao(mov.id, {
    status: 'perdido',
    devolucaoTipo: DEVOLUCAO_TIPO.perdido,
    dataDevolucao: now,
    observacao: observacao ?? null,
    classificacao: 'prejuizo',
    custoUnitarioNoMomento: custoUnit != null ? Number(custoUnit) : null,
    custoTotal: custoTotal,
    usuarioId: usuario?.usuarioId ?? null,
    usuarioNome: usuario?.usuarioNome ?? null
  })

  await addAuditLog({
    usuarioId: usuario?.usuarioId ?? null,
    usuarioNome: usuario?.usuarioNome ?? null,
    acao: 'EMPRESTIMO_MARCADO_PERDIDO',
    detalhes: JSON.stringify({ movementId }),
    tabelaAfetada: 'movimentacoes',
    registroId: mov.id
  })
}

export async function sendLoanToMaintenance({ usuario, movementId, observacao, quantidadeManutencao }) {
  const now = Date.now()
  // Buscar movimentação por ID direto
  const { data: movData } = await supabase.from('movimentacoes').select('id,status,produto_id,quantidade,funcionario_id,funcionario_nome').eq('id', movementId).single()
  if (!movData) throw new Error('Movimentação não encontrada.')
  const mov = snakeToCamel(movData)
  if (mov.status !== 'pendente-devolucao') throw new Error('Este item não está pendente.')

  // Buscar produto por ID direto
  const { data: prodData } = await supabase.from('produtos').select('id,nome').eq('id', mov.produtoId).single()
  if (!prodData) throw new Error('Produto não encontrado.')
  const produto = snakeToCamel(prodData)

  const qtyTotal = Number(mov.quantidade ?? 0)
  const qtdManutencao = quantidadeManutencao !== null ? Number(quantidadeManutencao) : qtyTotal
  const qtdRestante = qtyTotal - qtdManutencao

  // Se for manutenção parcial, criar nova movimentação para o restante pendente
  if (qtdRestante > 0) {
    await createMovimentacao({
      tipo: 'saida-emprestimo',
      produtoId: mov.produtoId,
      produtoNome: produto.nome,
      quantidade: qtdRestante,
      funcionarioId: mov.funcionarioId,
      funcionarioNome: mov.funcionarioNome,
      usuarioId: usuario?.usuarioId ?? null,
      usuarioNome: usuario?.usuarioNome ?? null,
      status: 'pendente-devolucao'
    })
    
    // Atualizar a movimentação original com a quantidade enviada para manutenção
    await updateMovimentacao(mov.id, {
      quantidade: qtdManutencao,
      status: 'em-manutencao',
      devolucaoTipo: DEVOLUCAO_TIPO.manutencao,
      dataDevolucao: now,
      observacao: observacao ?? null,
      dataEnvioManutencao: now,
      usuarioId: usuario?.usuarioId ?? null,
      usuarioNome: usuario?.usuarioNome ?? null,
      quantidadeManutencao: qtdManutencao
    })
  } else {
    // Se for manutenção total, atualiza a movimentação original
    await updateMovimentacao(mov.id, {
      status: 'em-manutencao',
      devolucaoTipo: DEVOLUCAO_TIPO.manutencao,
      dataDevolucao: now,
      observacao: observacao ?? null,
      dataEnvioManutencao: now,
      usuarioId: usuario?.usuarioId ?? null,
      usuarioNome: usuario?.usuarioNome ?? null,
      quantidadeManutencao: qtdManutencao
    })
  }

  await addAuditLog({
    usuarioId: usuario?.usuarioId ?? null,
    usuarioNome: usuario?.usuarioNome ?? null,
    acao: 'EMPRESTIMO_ENVIADO_MANUTENCAO',
    detalhes: JSON.stringify({ movementId }),
    tabelaAfetada: 'movimentacoes',
    registroId: mov.id
  })
}

// Registrar custo (opcional) de manutenção como movimentação financeira
export async function registerMaintenanceCost({ usuario, produto, manutencaoId, custo, fornecedor, nf, dataServico, observacao, quantidade }) {
  const valor = custo != null && custo !== '' ? Number(custo) : null
  const qty = quantidade != null && quantidade !== '' ? Number(quantidade) : 1
  return createMovimentacao({
    id: crypto.randomUUID(),
    tipo: 'manutencao',
    produtoId: produto.id,
    produtoNome: produto.nome,
    quantidade: qty,
    usuarioId: usuario?.usuarioId ?? null,
    usuarioNome: usuario?.usuarioNome ?? null,
    observacao: observacao ?? null,
    status: 'concluido',
    // Extras financeiros (opcionais)
    classificacao: 'manutencao',
    custoUnitarioNoMomento: valor && qty > 0 ? valor / qty : valor,
    custoTotal: valor,
    fornecedor: fornecedor || null,
    numeroDocumento: nf || null,
    dataServico: dataServico || Date.now(),
    manutencaoId: manutencaoId || null,
    evento: 'retorno'
  })
}

export async function returnFromMaintenance({ usuario, movementId, observacao, manutencao }) {
  const now = Date.now()
  // Buscar movimentação por ID direto
  const { data: movData } = await supabase.from('movimentacoes').select('id,status,produto_id,produto_nome,quantidade').eq('id', movementId).single()
  if (!movData) throw new Error('Movimentação não encontrada.')
  const mov = snakeToCamel(movData)
  if (mov.status !== 'em-manutencao') throw new Error('Este item não está em manutenção.')

  // Buscar produto por ID direto
  const { data: prodData } = await supabase.from('produtos').select('id,nome,quantidade').eq('id', mov.produtoId).single()
  if (!prodData) throw new Error('Produto não encontrado.')
  const produto = snakeToCamel(prodData)

  const atual = Number(produto.quantidade ?? 0)
  const qty = Number(mov.quantidade ?? 0)
  await updateProduto(produto.id, { quantidade: atual + qty })

  await updateMovimentacao(mov.id, {
    status: 'concluido',
    devolucaoTipo: DEVOLUCAO_TIPO.devolvido,
    dataDevolucao: now,
    observacao: observacao ?? null,
    dataRetornoManutencao: now,
    usuarioId: usuario?.usuarioId ?? null,
    usuarioNome: usuario?.usuarioNome ?? null
  })

  // Se veio dados de manutenção, registrar custo (opcional)
  if (manutencao && typeof manutencao === 'object') {
    const { custo, fornecedor, nf, dataServico, observacao: obsManutencao } = manutencao
    try {
      await registerMaintenanceCost({
        usuario,
        produto,
        manutencaoId: mov.id,
        custo,
        fornecedor,
        nf,
        dataServico,
        observacao: obsManutencao,
        quantidade: qty
      })
    } catch (e) {
      // Não bloquear retorno ao estoque se custo falhar
      console.error('[MANUTENCAO] Falha ao registrar custo opcional:', e)
    }
  }

  await addAuditLog({
    usuarioId: usuario?.usuarioId ?? null,
    usuarioNome: usuario?.usuarioNome ?? null,
    acao: 'MANUTENCAO_RETORNO_ESTOQUE',
    detalhes: JSON.stringify({ movementId, manutencao: manutencao ? { hasCusto: !!manutencao?.custo } : null }),
    tabelaAfetada: 'movimentacoes',
    registroId: mov.id
  })
}
